import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import './Analytics.css';

const Analytics = () => {
    const [timeRange, setTimeRange] = useState('all');
    const [selectedMetric, setSelectedMetric] = useState('completion');
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadAnalytics();
    }, [timeRange]);

    const loadAnalytics = async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/analytics', {
                params: { timeRange }
            });
            setAnalyticsData(response.data);
        } catch (error) {
            console.error('Error loading analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="analytics-loading">
                <div className="loading-spinner"></div>
                <p>Loading analytics...</p>
            </div>
        );
    }

    if (!analyticsData) {
        return (
            <div className="analytics-error">
                <p>Failed to load analytics data</p>
                <button onClick={loadAnalytics}>Retry</button>
            </div>
        );
    }

    const {
        totalPatients,
        activePatients,
        completedPatients,
        avgCompletion,
        completionDistribution,
        engagement,
        topSymptoms,
        recentActivity
    } = analyticsData;

    return (
        <div className="analytics">
            <div className="analytics-header">
                <h1>Analytics Dashboard</h1>
                <div className="analytics-controls">
                    <select
                        value={timeRange}
                        onChange={(e) => setTimeRange(e.target.value)}
                        className="time-range-select"
                    >
                        <option value="all">All Time</option>
                        <option value="7d">Last 7 Days</option>
                        <option value="30d">Last 30 Days</option>
                        <option value="90d">Last 90 Days</option>
                    </select>
                </div>
            </div>

            {/* Key Metrics */}
            <div className="key-metrics">
                <div className="metric-card primary">
                    <div className="metric-icon">👥</div>
                    <div className="metric-content">
                        <span className="metric-value">{totalPatients}</span>
                        <span className="metric-label">Total Patients</span>
                    </div>
                </div>

                <div className="metric-card success">
                    <div className="metric-icon">🟢</div>
                    <div className="metric-content">
                        <span className="metric-value">{activePatients}</span>
                        <span className="metric-label">Active This Week</span>
                    </div>
                </div>

                {/* <div className="metric-card warning">
                    <div className="metric-icon">📊</div>
                    <div className="metric-content">
                        <span className="metric-value">{avgCompletion}%</span>
                        <span className="metric-label">Avg. Completion</span>
                    </div>
                </div> */}

                <div className="metric-card info">
                    <div className="metric-icon">✅</div>
                    <div className="metric-content">
                        <span className="metric-value">{completedPatients}</span>
                        <span className="metric-label">Completed Program</span>
                    </div>
                </div>
            </div>

            <div className="analytics-grid">
                {/* Completion Distribution */}
                <div className="analytics-card">
                    <h3>Journal Completion Distribution</h3>
                    <div className="completion-chart">
                        {completionDistribution.map((range, index) => (
                            <div key={index} className="completion-bar">
                                <div className="bar-info">
                                    <span className="bar-label">{range.label}</span>
                                    <span className="bar-count">{range.count} patients</span>
                                </div>
                                <div className="bar-container">
                                    <div
                                        className="bar-fill"
                                        style={{
                                            width: totalPatients > 0 ? `${(range.count / totalPatients) * 100}%` : '0%',
                                            backgroundColor: range.color
                                        }}
                                    ></div>
                                </div>
                                <span className="bar-percentage">
                                    {totalPatients > 0 ? Math.round((range.count / totalPatients) * 100) : 0}%
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Engagement Metrics */}
                <div className="analytics-card">
                    <h3>Patient Engagement</h3>
                    <div className="engagement-metrics">
                        <div className="engagement-item">
                            <div className="engagement-number">{engagement.daily}</div>
                            <div className="engagement-label">Active Today</div>
                            <div className="engagement-bar">
                                <div
                                    className="engagement-fill"
                                    style={{ width: `${totalPatients > 0 ? (engagement.daily / totalPatients) * 100 : 0}%` }}
                                ></div>
                            </div>
                        </div>

                        <div className="engagement-item">
                            <div className="engagement-number">{engagement.weekly}</div>
                            <div className="engagement-label">Active This Week</div>
                            <div className="engagement-bar">
                                <div
                                    className="engagement-fill"
                                    style={{ width: `${totalPatients > 0 ? (engagement.weekly / totalPatients) * 100 : 0}%` }}
                                ></div>
                            </div>
                        </div>

                        <div className="engagement-item">
                            <div className="engagement-number">{engagement.monthly}</div>
                            <div className="engagement-label">Active This Month</div>
                            <div className="engagement-bar">
                                <div
                                    className="engagement-fill"
                                    style={{ width: `${totalPatients > 0 ? (engagement.monthly / totalPatients) * 100 : 0}%` }}
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Top Symptoms */}
                {/* <div className="analytics-card">
                    <h3>Most Common Symptoms</h3>
                    <div className="symptoms-list">
                        {topSymptoms.map((symptom, index) => (
                            <div key={index} className="symptom-item">
                                <div className="symptom-info">
                                    <span className="symptom-rank">#{index + 1}</span>
                                    <span className="symptom-name">{symptom.name}</span>
                                    <span className="symptom-count">{symptom.count} patients</span>
                                </div>
                                <div className="symptom-improvement">
                                    <span className="improvement-label">Avg. Improvement</span>
                                    <div className="improvement-bar">
                                        <div
                                            className="improvement-fill"
                                            style={{ width: `${symptom.improvement}%` }}
                                        ></div>
                                    </div>
                                    <span className="improvement-percentage">{symptom.improvement}%</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div> */}

                {/* Recent Activity */}
                <div className="analytics-card">
                    <h3>Recent Patient Activity</h3>
                    <div className="activity-feed">
                        {recentActivity && recentActivity.length > 0 ? (
                            recentActivity.map((activity, index) => (
                                <div key={index} className="activity-item">
                                    <div className="activity-avatar">
                                        {activity.patientName.split(' ').map(n => n[0]).join('').toUpperCase()}
                                    </div>
                                    <div className="activity-content">
                                        <span className="activity-patient">{activity.patientName}</span>
                                        <span className="activity-action">Updated journal</span>
                                        <span className="activity-time">
                                            {new Date(activity.lastActivity).toLocaleDateString()}
                                        </span>
                                    </div>
                                    <div className="activity-progress">
                                        {activity.progress}%
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="no-activity">
                                <span className="no-activity-icon">📝</span>
                                <p>No recent patient activity</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Analytics;